import { useState, useEffect } from 'react';
import { useEventBus } from '../hooks/useEventBus.js';
import SkyPlanner from './SkyPlanner.jsx';
import useSky from '../hooks/useSky.js';
import { tierColor as passTierColor } from '../utils/passTiers.js';

const API = import.meta.env.VITE_API_BASE || '/api';
const MONO = 'var(--mono)';
const C = {
  wrap: {
    width: '100%', height: '100%',
    background: 'var(--bg)',
    borderRight: '1px solid var(--hair)',
    display: 'flex', flexDirection: 'column',
    overflow: 'hidden', flexShrink: 0,
  },
  section: {
    borderBottom: '1px solid var(--hair)',
    padding: '8px 10px', flexShrink: 0,
  },
  sLabel: {
    color: 'var(--txt-dim)', fontSize: 'var(--ui-label)',
    letterSpacing: '0.16em', fontFamily: MONO,
    marginBottom: 6, textTransform: 'uppercase', fontWeight: 700,
    borderBottom: '1px solid var(--hair)', paddingBottom: 5,
  },
  row: {
    display: 'flex', alignItems: 'center',
    gap: 6, padding: '4px 0',
  },
  dot: (color, glow) => ({
    width: 7, height: 7, borderRadius: '50%',
    background: color, flexShrink: 0,
    boxShadow: glow ? `0 0 6px ${color}` : 'none',
  }),
  key: { color: 'var(--txt-dim)', fontSize: 'var(--ui-body)', fontFamily: MONO, flex: 1 },
  val: { color: 'var(--txt)', fontSize: 'var(--ui-body)', fontFamily: MONO, fontWeight: 500 },
  valHi: { color: 'var(--txt-bright)', fontSize: 'var(--ui-body)', fontFamily: MONO, fontWeight: 700 },
  tierBadge: (t) => {
    const color = passTierColor(t) ?? passTierColor(1);
    return {
      background: 'var(--bg-2)', border: `1px solid ${color}`,
      borderRadius: 2, padding: '2px 5px',
      color, fontSize: 'var(--ui-small)', fontFamily: MONO,
      letterSpacing: '0.07em', flexShrink: 0,
    };
  },
  btn: {
    width: '100%', padding: '6px 0',
    background: 'transparent',
    border: '1px solid var(--hair-2)',
    borderRadius: 2, color: 'var(--txt-dim)',
    fontSize: 'var(--ui-label)', fontFamily: MONO, fontWeight: 600,
    letterSpacing: '0.12em', cursor: 'pointer',
    marginTop: 6,
  },
  btnActive: {
    width: '100%', padding: '6px 0',
    background: 'var(--amber-2)',
    border: '1px solid var(--accent)',
    borderRadius: 2, color: 'var(--accent)',
    fontSize: 'var(--ui-label)', fontFamily: MONO, fontWeight: 700,
    letterSpacing: '0.12em', cursor: 'pointer',
    marginTop: 6,
  },
  evFeed: { flex: 1, overflowY: 'auto', padding: '4px 0' },
  evRow: {
    display: 'flex', alignItems: 'flex-start',
    gap: 6, padding: '6px 10px',
    borderBottom: '1px solid var(--hair)',
  },
  evBar: (sev) => {
    const colors = { critical: 'var(--red)', warning: 'var(--warn)', notice: 'var(--green)', info: 'var(--cyan)' };
    return { width: 2, minHeight: 22, borderRadius: 1,
             background: colors[sev] || 'var(--hair-2)', flexShrink: 0, marginTop: 2 };
  },
  evCat:   { color: 'var(--txt-dim)', fontSize: 'var(--ui-label)', fontFamily: MONO, letterSpacing: '0.12em', fontWeight: 600 },
  evTitle: { color: 'var(--txt)', fontSize: 'var(--ui-label)', fontFamily: MONO, lineHeight: 1.4 },
  evTime:  { color: 'var(--txt-dim)', fontSize: 'var(--ui-label)', fontFamily: MONO, marginLeft: 'auto', flexShrink: 0 },
};

function timeAgo(ts) {
  const s = Math.floor((Date.now() - new Date(ts).getTime()) / 1000);
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s/60)}m`;
  return `${Math.floor(s/3600)}h`;
}

function darknessColor(label) {
  if (!label) return 'var(--hair-2)';
  const l = label.toLowerCase();
  if (l.includes('astro')) return 'var(--cyan)';
  if (l.includes('naut')) return 'var(--teal)';
  if (l.includes('civil')) return 'var(--green)';
  return 'var(--warn)';
}


/**
 * MoonGlyph - tiny inline SVG of the current moon phase.
 *
 * phaseFraction: 0..1 (0=new, 0.5=quarter, 1=full)
 * phaseAngle:    0..360 deg (astronomy-engine convention)
 *                0 = new, 90 = first quarter (waxing), 180 = full,
 *                270 = last quarter (waning)
 *
 * Rendered as a 12x12 dark disc with the illuminated portion drawn as a
 * lighter shape. The terminator (light/dark boundary) is an ellipse whose
 * horizontal radius depends on phase. Waning phases are mirrored.
 */
function MoonGlyph({ phaseFraction, phaseAngle, size = 12 }) {
  if (phaseFraction == null) return null;

  // Clamp inputs
  const p = Math.max(0, Math.min(1, phaseFraction));
  const waxing = (phaseAngle == null) || (phaseAngle < 180);

  const r = size / 2;        // disc radius
  const cx = r, cy = r;      // center
  // Terminator ellipse horizontal radius: 0 at quarter, r at new/full
  const tr = r * Math.abs(1 - 2 * p);
  const gibbous = p > 0.5;
  const outerSweep = waxing ? 1 : 0;
  const innerSweep = (gibbous === waxing) ? 0 : 1;

  const path =
    'M ' + cx + ' ' + (cy - r) + ' ' +
    'A ' + r + ' ' + r + ' 0 0 ' + outerSweep + ' ' + cx + ' ' + (cy + r) + ' ' +
    'A ' + tr + ' ' + r + ' 0 0 ' + innerSweep + ' ' + cx + ' ' + (cy - r) + ' Z';

  return (
    <svg width={size} height={size} viewBox={'0 0 ' + size + ' ' + size} style={{ flexShrink: 0 }}>
      <circle cx={cx} cy={cy} r={r - 0.5} fill="var(--hair-2)" />
      <path d={path} fill="var(--txt)" />
    </svg>
  );
}
export default function LeftConsole({ observer, setObserver, passes, visibleNow,
                                      weather, onGoto, visibleOnly, onVisibleToggle, onEditLocation }) {
  const { events, ack } = useEventBus();
  const [plannerOpen, setPlannerOpen] = useState(false);
  const [tick, setTick] = useState(0);  // drives live pass countdowns

  // 1-second ticker for countdown display
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  function fmtCountdown(iso) {
    const diff = new Date(iso) - Date.now();
    if (diff <= 0) return 'NOW';
    const m = Math.floor(diff / 60000);
    const s = Math.floor((diff % 60000) / 1000);
    if (m > 90) return `${Math.floor(m / 60)}h ${m % 60}m`;
    if (m > 0)  return `${m}m ${s}s`;
    return `${s}s`;
  }

  const { bodies: skyBodies } = useSky(observer);
  const darkness = observer ? (weather?.darkness || '-') : '-';
  const sunEl = observer && weather?.observer_sun_el != null
    ? `${parseFloat(weather.observer_sun_el).toFixed(1)}°` : '-';

  const topPasses = (passes || []).slice(0, 6);

  const unackedCount = events.filter(e => !e.acknowledged).length;

  return (
    <div style={C.wrap}>


      {/* Observer section */}
      <div style={C.section}>
        <div style={C.sLabel}>Observer</div>
        {observer ? (
          <>
            <div style={C.row}>
              <div style={C.dot('var(--green)', true)} />
              <span style={C.key}>Location</span>
              <span style={C.valHi}>{observer.lat.toFixed(2)}°</span>
            </div>
            <div style={C.row}>
              <div style={C.dot(darknessColor(darkness), false)} />
              <span style={C.key}>Sky</span>
              <span style={C.val}>{darkness}</span>
            </div>
            <div style={C.row}>
              <div style={C.dot('var(--cyan)', false)} />
              <span style={C.key}>Sun el</span>
              <span style={C.val}>{sunEl}</span>
            </div>
            {visibleNow != null && (
              <div style={C.row}>
                <div style={C.dot('var(--cyan)', true)} />
                <span style={C.key}>Above horizon · sunlit</span>
                <span style={C.valHi}>{visibleNow.toLocaleString()}</span>
              </div>
            )}
            <button
              style={visibleOnly ? C.btnActive : C.btn}
              onClick={onVisibleToggle} aria-pressed={visibleOnly} title="Filter by horizon position; visibility also depends on sunlight, darkness, brightness and clouds"
            >ABOVE HORIZON ONLY</button>
            {observer && (
              <button style={C.btnActive} onClick={() => setPlannerOpen(true)}>
                TONIGHT'S SKY
              </button>
            )}
            <button
              style={C.btn}
              onClick={onEditLocation}
            >CHANGE LOCATION</button>
          </>
        ) : (
          <div className="og-location-prompt"><p>Set your location for passes and sky conditions.</p><button className="og-primary" onClick={onEditLocation}>Set location</button></div>
        )}
      </div>


      {/* Sky Tonight - planets, sun, moon for the observer.
          Number shown is ALTITUDE (degrees above horizon). 0=horizon, 90=zenith. */}
      {observer && skyBodies && skyBodies.length > 0 && (
        <div style={C.section} title="Bodies currently visible from your location. Number is altitude above horizon: 0 deg = horizon, 90 deg = directly overhead.">
          <div style={C.sLabel}>Sky Tonight</div>
          <div style={{ fontSize: 'var(--ui-label)', color: 'var(--txt-dim)', marginBottom: 4, letterSpacing: '0.06em' }}>
            BODY · ALT °  (above horizon)
          </div>
          {skyBodies.filter(b => b.above_horizon).slice(0, 6).map(b => {
            const dotColor =
              b.type === 'sun'   ? 'var(--accent)' :
              b.type === 'moon'  ? 'var(--txt)' :
                                   'var(--cyan)';
            const phaseLabel = b.phase_fraction != null
              ? Math.round(b.phase_fraction * 100) + '%'
              : null;
            const detail = b.type === 'moon'
              ? b.name + ' - ' + Math.round(b.phase_fraction * 100) + '% illuminated, altitude ' + b.elevation_deg.toFixed(0) + ' deg, azimuth ' + b.azimuth_deg.toFixed(0) + ' deg'
              : b.name + ' - altitude ' + b.elevation_deg.toFixed(0) + ' deg, azimuth ' + b.azimuth_deg.toFixed(0) + ' deg, magnitude ' + (b.magnitude != null ? b.magnitude.toFixed(1) : '--');
            return (
              <div key={b.name} style={C.row} title={detail}>
                <div style={C.dot(dotColor, b.elevation_deg > 30)} />
                <span style={C.key}>{b.name}</span>
{phaseLabel && (
                  <>
                    {b.type === 'moon' && (
                      <MoonGlyph phaseFraction={b.phase_fraction} phaseAngle={b.phase_angle_deg} size={11} />
                    )}
                    <span style={{ fontSize: 'var(--ui-label)', color: 'var(--txt-dim)', margin: '0 4px 0 3px' }}>{phaseLabel}</span>
                  </>
                )}
                <span style={C.val}>{b.elevation_deg.toFixed(0)}°</span>
              </div>
            );
          })}
          {skyBodies.filter(b => b.above_horizon).length === 0 && (
            <div style={{ ...C.row, color: 'var(--txt-dim)', fontSize: 'var(--ui-label)' }}>
              Nothing above horizon
            </div>
          )}
        </div>
      )}
      {/* Tonight's passes, shown when observer set; clickable rows fly camera to satellite */}
      {observer && (
        <div style={C.section}>
          <div style={C.sLabel}>Tonight's Best</div>
          {topPasses.length === 0 ? (
            <div style={{ color: 'var(--txt-dim)', fontSize: 'var(--ui-label)', fontFamily: MONO,
                          padding: '4px 0', lineHeight: 1.6 }}>
              No visible passes in next 12h
            </div>
          ) : (
            topPasses.map((p, i) => (
              <button type="button"
                key={i}
                style={{ width:"100%", border:0, color:"var(--txt)", background:"transparent", textAlign:"left", ...C.row, padding: '5px 0', cursor: 'pointer',
                         borderRadius: 2, margin: '1px -4px', paddingLeft: 4, paddingRight: 4 }}
                onClick={() => onGoto({ norad_1: p.norad_id, title: p.name })}
              >
                <span style={C.tierBadge(p.visibility_tier)}>T{p.visibility_tier}</span>
                <div style={{ flex: 1, marginLeft: 4, overflow: 'hidden' }}>
                  <div style={{ ...C.valHi, fontSize: 'var(--ui-label)', whiteSpace: 'nowrap',
                                overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {p.name}
                  </div>
                  <div style={{ ...C.val, fontSize: 'var(--ui-label)' }}>
                    {p.start ? new Date(p.start).toLocaleTimeString([], {hour:'2-digit',minute:'2-digit'}) : '-'}
                    {p.max_el != null ? ` · ${Math.round(p.max_el)}°` : ''}
                  </div>
                </div>
                {p.start && (
                  <div style={{ color: 'var(--accent)', fontSize: 'var(--ui-label)', fontFamily: MONO,
                                fontWeight: 700, flexShrink: 0, marginLeft: 4 }}>
                    {fmtCountdown(p.start)}
                  </div>
                )}
              </button>
            ))
          )}
        </div>
      )}


      {/* Event feed */}
      <div style={{ ...C.section, marginBottom: 0, borderBottom: 'none', paddingBottom: 2 }}>
        <div style={{ ...C.sLabel, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Events</span>
          {unackedCount > 0 && (
            <span style={{ color: 'var(--red)', fontSize: 'var(--ui-label)', fontWeight: 700 }}>{unackedCount} NEW</span>
          )}
        </div>
      </div>
      <div style={C.evFeed}>
        {events.length === 0 && (
          <div style={{ padding: '8px 10px', color: 'var(--txt-dim)', fontSize: 'var(--ui-label)', fontFamily: MONO }}>
            Monitoring active.
          </div>
        )}
        {events.map(ev => (
          <button type="button" key={ev.id} style={{
            ...C.evRow, width:"100%", border:0, textAlign:"left", color:"var(--txt)",
            background: ev.acknowledged ? 'transparent' : 'rgba(232,227,216,0.02)',
            cursor: ev.norad_1 ? 'pointer' : 'default',
            opacity: ev.acknowledged ? 0.45 : 1,
          }}
            onClick={() => { if (ev.norad_1 || ev.lat) onGoto(ev); ack(ev.id); }}
          >
            <div style={C.evBar(ev.severity)} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={C.evCat}>{ev.category?.toUpperCase()}</div>
              <div style={C.evTitle}>{ev.title}</div>
              {ev.body && <div style={{ color: 'var(--txt-dim)', fontFamily: MONO, fontSize: 'var(--ui-small)', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ev.body}</div>}
            </div>
            <div style={C.evTime}>{timeAgo(ev.ts)}</div>
          </button>
        ))}
      </div>

      {plannerOpen && observer && (
        <SkyPlanner open observer={observer} onGoto={onGoto} onClose={() => setPlannerOpen(false)} />
      )}
    </div>
  );
}
